import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import QuotationForm from "./QuotationForm";
import { getQuotations, deleteQuotation, openQuotationPdf } from "../api/quotationService";

const QuotationList = () => {
    const [quotations, setQuotations] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [editingQuotation, setEditingQuotation] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loadingPdfId, setLoadingPdfId] = useState(null);

    const itemsPerPage = 20;

    // 🔄 FETCH (SERVER-SIDE PAGINATION)
    const fetchQuotations = async (page = 1) => {
        try {
            setLoading(true);

            const result = await getQuotations(page, itemsPerPage);

            setQuotations(result.data || []);
            setCurrentPage(result.currentPage || 1);
            setTotalPages(result.totalPages || 1);

        } catch (error) {
            toast.error("Error fetching quotations.");
            console.error(error);
            setQuotations([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchQuotations(currentPage);
    }, [currentPage]);

    const handleAddNew = () => {
        setEditingQuotation(null);
        setShowForm(true);
    };

    const handleEdit = (quotation) => {
        setEditingQuotation(quotation);
        setShowForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this quotation?")) return;

        try {
            const response = await deleteQuotation(id);
            if (response) {
                toast.success("Quotation deleted successfully ✅");
                fetchQuotations(currentPage);
            } else {
                toast.error(response?.message || "Failed to delete quotation ❌");
            }
        } catch (error) {
            toast.error("Error deleting quotation.");
            console.error(error);
        }
    };

    // 🖨️ PDF
    const openPdf = async (id) => {
        try {
            setLoadingPdfId(id);
            await openQuotationPdf(id);
        } catch (err) {
            toast.error("Failed to open PDF");
        } finally {
            setLoadingPdfId(null);
        }
    };

    const handleFormClose = () => {
        setShowForm(false);
        setEditingQuotation(null);
        fetchQuotations(currentPage);
    };

    // 🔍 SEARCH (current page only – consistent with server pagination)
    const filteredQuotations = quotations.filter((q) => {
        const query = searchQuery.toLowerCase();
        return (
            q.customerFullName?.toLowerCase().includes(query) ||
            q.quotationNumber?.toLowerCase().includes(query) ||
            q.moduleMake?.toLowerCase().includes(query)
        );
    });

    return (
        <div className="p-1 max-w-8xl mx-auto">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-gray-800">
                    {showForm
                        ? editingQuotation ? "📝 Edit Quotation" : "➕ Add Quotation"
                        : "📄 Quotations List"}
                </h2>

                {!showForm && (
                    <button
                        onClick={handleAddNew}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow"
                    >
                        Add Quotation
                    </button>
                )}
            </div>

            {/* LIST */}
            {!showForm && (
                <>
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search quotations..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full max-w-md px-4 py-2 border rounded-lg shadow-sm focus:ring-2 focus:ring-blue-300"
                        />
                    </div>

                    <div className="overflow-x-auto bg-white shadow rounded-lg">
                        <table className="min-w-full text-sm">
                            <thead className="bg-blue-600 text-white">
                                <tr>
                                    <th className="px-4 py-3 text-left font-semibold">Quotation #</th>
                                    <th className="px-4 py-3 text-left font-semibold">Customer</th>
                                    <th className="px-4 py-3 text-left font-semibold">Amount (₹)</th>
                                    <th className="px-4 py-3 text-left font-semibold">Date</th>
                                    <th className="px-4 py-3 text-center font-semibold">Actions</th>
                                </tr>
                            </thead>

                            <tbody className="divide-y divide-gray-200">
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-6">
                                            Loading quotations...
                                        </td>
                                    </tr>
                                ) : filteredQuotations.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="text-center py-6">
                                            No quotations found.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredQuotations.map((q) => {
                                        const isLoadingPdf = loadingPdfId === q.id;

                                        return (
                                            <tr key={q.id} className="hover:bg-gray-50 transition">
                                                <td className="px-4 py-2">{q.quotationNumber}</td>
                                                <td className="px-4 py-2">{q.customerFullName}</td>
                                                <td className="px-4 py-2">
                                                    ₹{q.amount?.toLocaleString("en-IN")}
                                                </td>
                                                <td className="px-4 py-2">
                                                    {q.date
                                                        ? new Date(q.date).toLocaleDateString("en-GB")
                                                        : ""}
                                                </td>

                                                <td className="px-4 py-2 text-center">
                                                    <div className="flex gap-3 justify-center">
                                                        {/* 🖨️ PRINT */}
                                                        <button
                                                            onClick={() => openPdf(q.id)}
                                                            className="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                                                            title="Print / Download PDF"
                                                            disabled={isLoadingPdf}
                                                        >
                                                            {isLoadingPdf ? (
                                                                <span className="animate-spin inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full" />
                                                            ) : (
                                                                "🖨️"
                                                            )}
                                                        </button>

                                                        {/* ✏️ EDIT */}
                                                        <button
                                                            onClick={() => handleEdit(q)}
                                                            className="text-green-600 hover:text-green-800"
                                                            title="Edit"
                                                        >
                                                            ✏️
                                                        </button>

                                                        {/* 🗑️ DELETE */}
                                                        <button
                                                            onClick={() => handleDelete(q.id)}
                                                            className="text-red-600 hover:text-red-800"
                                                            title="Delete"
                                                        >
                                                            🗑️
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* PAGINATION */}
                    {totalPages > 1 && (
                        <div className="mt-6 flex justify-center space-x-1">
                            <button
                                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
                                disabled={currentPage === 1}
                                className="px-3 py-1 border rounded-l text-sm bg-white hover:bg-gray-100 disabled:opacity-50"
                            >
                                Prev
                            </button>

                            {[...Array(totalPages)].map((_, index) => (
                                <button
                                    key={index}
                                    onClick={() => setCurrentPage(index + 1)}
                                    className={`px-3 py-1 border text-sm ${currentPage === index + 1
                                        ? "bg-blue-600 text-white"
                                        : "bg-white hover:bg-gray-100"
                                        }`}
                                >
                                    {index + 1}
                                </button>
                            ))}

                            <button
                                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className="px-3 py-1 border rounded-r text-sm bg-white hover:bg-gray-100 disabled:opacity-50"
                            >
                                Next
                            </button>
                        </div>
                    )}
                </>
            )}

            {/* FORM */}
            {showForm && (
                <div className="mt-6 bg-white shadow rounded-lg p-6">
                    <QuotationForm
                        initialData={editingQuotation}
                        onClose={handleFormClose}
                    />
                </div>
            )}
        </div>
    );
};

export default QuotationList;
